import { Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Company, IField, Smartphoto, SmartphotoCompanyFields } from '@bdeo-motor/smartphoto-entities';
import { SelectorService } from '../../services/selector.service';
import { BdeoDrawer, BdeoToastService } from '@bdeo/webcomponents';
import { SmartphotoService } from '../../services/smartphoto.service';
import { I18NEXT_SERVICE } from 'angular-i18next';

@Component({
  selector: 'app-smartphoto-review-drawer',
  templateUrl: './smartphoto-review-drawer.component.html',
  styleUrl: './smartphoto-review-drawer.component.scss'
})
export class SmartphotoReviewDrawerComponent implements OnChanges {
  @Input({ required: true }) public smartphoto?: Smartphoto;
  @Input({ required: true }) public company?: Company;
  @ViewChild('drawer') private drawer?: ElementRef<BdeoDrawer>;

  private readonly selectorService = inject(SelectorService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly smartphotoService = inject(SmartphotoService);
  private readonly i18NextService = inject(I18NEXT_SERVICE);

  protected inProgress = false;
  protected hasAdditionals = false;
  protected fields: IField[] = [];
  protected additionalsForm: FormGroup = this.formBuilder.group({
    emails: ['', [this.validateAdditionalsEmails.bind(this)]],
    notes: ['', []]
  });
  protected reviewForm: FormGroup = new FormGroup({});

  private validateAdditionalsEmails(control: AbstractControl<string[] | ''>): ValidationErrors | null {
    if (control.value === '') {
      return null;
    }

    const validEmails = control.value.every((email) => Validators.email(new FormControl(email.toLowerCase())) === null);
    return validEmails ? null : { invalidEmails: true };
  }

  public async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (!this.smartphoto || !this.company || (!changes['smartphoto'] && !changes['company'])) {
      return;
    }

    const originalSmartphoto = this.smartphotoService.getOriginalSmartphoto();
    this.hasAdditionals = this.company.smartphoto?.additionals?.active ?? false;
    this.additionalsForm.get('emails')?.updateValueAndValidity();

    if (originalSmartphoto) {
      const companyFields = new SmartphotoCompanyFields(
        originalSmartphoto,
        {
          closingFields: this.company.closingFields,
          createFields: this.company.createFields,
          selfAdjustFields: this.company.selfAdjustFields,
          cancellationFields: this.company.cancellationFields
        },
        {}
      );

      const promiseSelector = Promise.all(
        companyFields.closing.map(async (field) => {
          if (field.type === 'list') {
            const listName = this.company?.closingFields?.find((f) => f.fieldName === field.properties[0])?.listName;
            return {
              ...field,
              options: listName
                ? (await this.selectorService.get(listName)).map((opt) => ({
                    ...opt,
                    text: opt.name
                  }))
                : undefined
            };
          }

          return field;
        })
      );

      this.fields = await promiseSelector;
    }

    const reviewControls: Record<string, [string, ValidatorFn[]]> = {};
    this.fields.forEach((field) => {
      reviewControls[field.properties[0]] = ['', field.required ? [Validators.required] : []];
    });
    this.reviewForm = this.formBuilder.group(reviewControls);
  }

  public open(): void {
    if (this.drawer) {
      this.drawer.nativeElement.show = true;
    }
  }

  public close(): void {
    if (this.drawer) {
      this.drawer.nativeElement.show = false;
    }
  }

  protected async review(): Promise<void> {
    try {
      this.inProgress = true;
      await this.smartphotoService.update.status(5, {
        ...this.reviewForm.value,
        additionals: this.hasAdditionals ? this.additionalsForm.value : undefined
      });
      // TODO: Podemos ahorrarnos este GET si cambiamos el back para que selfadjust-update devuelva el objeto actualizado
      // Ahora mismo no podemos saber qué cambios ha hecho el back realmente a no ser que hagamos un GET
      const smartphotoId = this.smartphoto?.id;
      if (smartphotoId) {
        await this.smartphotoService.get.it(smartphotoId);
      }

      BdeoToastService.success(this.i18NextService.t('brokers:success_update_smartphoto'));
      this.close();
    } catch (e: unknown) {
      BdeoToastService.error(this.i18NextService.t('brokers:errors.review'));
    } finally {
      this.inProgress = false;
    }
  }
}
